<template>
    <CRow>
        <CCol col="12" xl="12">
            <CCard>
                <CCardHeader>
                    <strong>Результаты форм</strong>
                </CCardHeader>
                <CCardBody>
                    <CRow class="mb-3 align-items-end">
                        <CCol sm="12" md="4">
                            <label class="font-weight-bold d-block mb-1" style="font-size:13px">Телефон</label>
                            <CInput
                                v-model="phoneInput"
                                class="mb-0"
                                type="text"
                                placeholder="Поиск по телефону"
                                @keyup.enter="applyFilters"
                            />
                        </CCol>
                        <CCol sm="12" md="4">
                            <label class="font-weight-bold d-block mb-1" style="font-size:13px">Форма</label>
                            <CSelect
                                :value.sync="formInput"
                                :options="formOptions"
                                class="mb-0"
                                placeholder="Все формы"
                                @change="applyFilters"
                            />
                        </CCol>
                        <CCol sm="12" md="4" class="d-flex align-items-end">
                            <CButton color="primary" class="mb-0" @click="applyFilters">
                                Найти
                            </CButton>
                        </CCol>
                    </CRow>

                    <!-- Фильтр по статусу -->
                    <div class="btn-group mb-3" role="group">
                        <CButton
                            v-for="tab in statusTabs"
                            :key="tab.value"
                            :color="statusFilter === tab.value ? 'primary' : 'secondary'"
                            size="sm"
                            @click="statusFilter = tab.value"
                        >
                            {{ tab.label }}
                        </CButton>
                    </div>

                    <div v-if="loading" class="table__preloader">
                        <CSpinner color="primary" />
                    </div>

                    <template v-else>
                        <CDataTable
                            hover
                            :items="items"
                            :fields="fields"
                            :items-per-page="pageSize"
                            no-items-view-text="Нет данных"
                        >
                            <template #phone="data">
                                <td>
                                    <span>{{ data.item.phone_normalized || data.item.phone }}</span>
                                    <router-link
                                        v-if="data.item.customer_id"
                                        :to="{ name: 'Customer', params: { id: data.item.customer_id } }"
                                        target="_blank"
                                        class="customer-link ml-1"
                                        title="Карточка клиента"
                                    >↗</router-link>
                                    <span v-else class="text-muted ml-1" style="font-size:11px" title="Клиент не зарегистрирован">нет в системе</span>
                                </td>
                            </template>

                            <template #file="data">
                                <td>
                                    <div v-if="getFileUrl(data.item)" class="img-preview">
                                        <img
                                            :src="getFileUrl(data.item)"
                                            alt="Файл формы"
                                            class="img-thumb"
                                        >
                                        <img
                                            :src="getFileUrl(data.item)"
                                            alt="Файл формы"
                                            class="img-full"
                                        >
                                    </div>
                                    <span v-else class="text-muted">-</span>
                                </td>
                            </template>

                            <template #pairs="data">
                                <td>
                                    <div class="pairs-list">
                                        <div v-for="pair in getOtherPairs(data.item)" :key="pair.key" class="pair-row">
                                            <strong>{{ pair.label }}:</strong> {{ pair.value }}
                                        </div>
                                        <span v-if="!getOtherPairs(data.item).length" class="text-muted">-</span>
                                    </div>
                                </td>
                            </template>

                            <template #status="data">
                                <td>
                                    <CBadge :color="statusColor(data.item.status)" class="mb-1">
                                        {{ statusLabel(data.item.status) }}
                                    </CBadge>
                                    <div class="d-flex flex-wrap gap-1 mt-1">
                                        <CButton
                                            v-if="data.item.status !== 'approved'"
                                            size="sm"
                                            color="success"
                                            :disabled="updatingId === data.item.id"
                                            @click="setStatus(data.item, 'approved')"
                                        >
                                            Одобрить
                                        </CButton>
                                        <CButton
                                            v-if="data.item.status !== 'rejected'"
                                            size="sm"
                                            color="danger"
                                            :disabled="updatingId === data.item.id"
                                            @click="setStatus(data.item, 'rejected')"
                                        >
                                            Отклонить
                                        </CButton>
                                        <CButton
                                            v-if="data.item.status === 'approved'"
                                            size="sm"
                                            color="info"
                                            @click="openIikoModal(data.item)"
                                        >
                                            Категории iiko
                                        </CButton>
                                    </div>
                                </td>
                            </template>
                        </CDataTable>

                        <CPagination
                            v-if="totalPages > 1"
                            :active-page.sync="activePage"
                            :pages="totalPages"
                            align="center"
                            class="mt-3"
                        />
                    </template>
                </CCardBody>
            </CCard>
        </CCol>

        <!-- Модал управления категориями iiko -->
        <CModal
            :show.sync="iikoModalVisible"
            title="Категории iiko"
            size="lg"
            :close-on-backdrop="false"
        >
            <template v-if="iikoModalItem">
                <div class="mb-3">
                    <strong>{{ iikoModalItem.phone }}</strong>
                    <span v-if="iikoModalItem.data && iikoModalItem.data.name" class="text-muted ml-2">
                        {{ iikoModalItem.data.name }}
                    </span>
                </div>

                <div v-if="iikoCustomerLoading" class="d-flex align-items-center gap-2 mb-3">
                    <CSpinner size="sm" /> Загрузка клиента...
                </div>
                <div v-else-if="iikoCustomerError" class="text-danger mb-3">
                    {{ iikoCustomerError }}
                </div>

                <template v-else-if="iikoCustomer">
                    <div class="mb-3 text-sm text-muted">
                        {{ iikoCustomer.name }} {{ iikoCustomer.surname }}
                        <span class="ml-2 text-xs">ID: {{ iikoCustomer.id }}</span>
                    </div>

                    <!-- Добавить категорию -->
                    <div class="d-flex gap-2 mb-3 align-items-end">
                        <div class="flex-1">
                            <label class="font-weight-bold d-block mb-1" style="font-size:13px">Добавить категорию</label>
                            <select v-model="iikoSelectedCategoryId" class="form-control form-control-sm">
                                <option value="">— выберите категорию —</option>
                                <option
                                    v-for="cat in iikoAvailableToAdd"
                                    :key="cat.id"
                                    :value="cat.iiko_category_id"
                                >
                                    {{ cat.name }}{{ cat.title ? ` (${cat.title})` : '' }}
                                </option>
                            </select>
                        </div>
                        <CButton
                            color="success"
                            size="sm"
                            :disabled="!iikoSelectedCategoryId || iikoActionLoading"
                            @click="addIikoCategory"
                        >
                            <CSpinner v-if="iikoActionLoading" size="sm" class="mr-1" />
                            Добавить
                        </CButton>
                    </div>

                    <!-- Текущие категории -->
                    <label class="font-weight-bold d-block mb-1" style="font-size:13px">Текущие категории</label>
                    <div v-if="iikoCustomer.categories && iikoCustomer.categories.length" class="d-flex flex-wrap gap-2">
                        <div
                            v-for="cat in iikoCustomer.categories"
                            :key="cat.id"
                            class="d-flex align-items-center badge-cat"
                        >
                            <span>{{ cat.name }}</span>
                            <button
                                class="btn-remove-cat"
                                title="Удалить"
                                :disabled="iikoActionLoading"
                                @click="removeIikoCategory(cat.id)"
                            >×</button>
                        </div>
                    </div>
                    <div v-else class="text-muted" style="font-size:13px">Нет категорий</div>
                </template>
            </template>

            <template #footer>
                <CButton color="secondary" @click="iikoModalVisible = false">Закрыть</CButton>
            </template>
        </CModal>
    </CRow>
</template>

<script>
const { formatDate } = require('../../utils/views');

const STATUS_TABS = [
    { value: 'all', label: 'Все' },
    { value: 'pending', label: 'Ожидают' },
    { value: 'approved', label: 'Одобренные' },
    { value: 'rejected', label: 'Отклонённые' },
];

export default {
    name: 'FormResults',

    data() {
        return {
            loading: false,
            items: [],
            total: 0,
            pageSize: 20,
            updatingId: null,
            statusTabs: STATUS_TABS,
            phoneInput: '',
            formInput: '',
            formOptions: [{ value: '', label: 'Все формы' }],
            fields: [
                { key: 'id', label: 'ID', _classes: 'font-weight-bold' },
                { key: 'phone', label: 'Телефон' },
                { key: 'form_code', label: 'Код формы' },
                { key: 'file', label: 'Картинка' },
                { key: 'pairs', label: 'Поля формы' },
                { key: 'status', label: 'Статус' },
            ],

            // iiko modal
            iikoModalVisible: false,
            iikoModalItem: null,
            iikoCategories: [],
            iikoCustomer: null,
            iikoCustomerLoading: false,
            iikoCustomerError: null,
            iikoSelectedCategoryId: '',
            iikoActionLoading: false,
        };
    },

    computed: {
        activePage: {
            get() {
                return parseInt(this.$route.query.page) || 1;
            },
            set(val) {
                this.$router.replace({ query: { ...this.$route.query, page: val } });
            },
        },
        statusFilter: {
            get() {
                return this.$route.query.status || 'all';
            },
            set(val) {
                const query = { ...this.$route.query, page: 1 };
                if (val === 'all') {
                    delete query.status;
                } else {
                    query.status = val;
                }
                this.$router.replace({ query });
            },
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.total / this.pageSize));
        },
        iikoAvailableToAdd() {
            if (!this.iikoCustomer || !this.iikoCategories.length) return [];
            const existing = new Set((this.iikoCustomer.categories || []).map(c => c.id));
            return this.iikoCategories.filter(c => !existing.has(c.iiko_category_id));
        },
    },

    watch: {
        '$route.query': {
            handler(query) {
                const phone = query.phone || '';
                if (phone !== this.phoneInput) {
                    this.phoneInput = phone;
                }
                const formCode = query.form_code || '';
                if (formCode !== this.formInput) {
                    this.formInput = formCode;
                }
                this.loadData();
            },
        },
    },

    async mounted() {
        this.phoneInput = this.$route.query.phone || '';
        this.formInput = this.$route.query.form_code || '';
        await this.loadFormOptions();
        await this.loadData();
    },

    methods: {
        formatDate(date) {
            return formatDate(date);
        },

        statusColor(status) {
            if (status === 'approved') return 'success';
            if (status === 'rejected') return 'danger';
            return 'secondary';
        },

        statusLabel(status) {
            if (status === 'approved') return 'Одобрен';
            if (status === 'rejected') return 'Отклонён';
            return 'Ожидает';
        },

        getFileUrl(item) {
            const data = item && item.data ? item.data : {};
            const fileValue = data.file;
            if (!fileValue) return '';

            let filePath = '';
            if (typeof fileValue === 'string') {
                filePath = fileValue;
            } else if (fileValue && typeof fileValue === 'object') {
                filePath = fileValue.url || fileValue.path || '';
            }

            if (!filePath) return '';
            if (/^https?:\/\//.test(filePath)) return filePath;

            const normalizedPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
            return `${process.env.VUE_APP_STATIC_BASE_URL}${normalizedPath}`;
        },

        getOtherPairs(item) {
            const data = item && item.data && typeof item.data === 'object' ? item.data : {};
            const excluded = ['file', 'phone', 'form_code', 'uinfo', 'marketing_consent', 'personal_data_consent'];

            return Object.keys(data)
                .filter((key) => !excluded.includes(key))
                .map((key) => ({
                    key,
                    label: this.humanizeKey(key),
                    value: this.formatPairValue(data[key]),
                }));
        },

        humanizeKey(key) {
            return String(key || '')
                .replace(/_/g, ' ')
                .trim();
        },

        formatPairValue(value) {
            if (value === null || value === undefined || value === '') return '-';
            if (typeof value === 'object') {
                try {
                    return JSON.stringify(value);
                } catch (e) {
                    return String(value);
                }
            }
            return String(value);
        },

        applyFilters() {
            const trimmedPhone = String(this.phoneInput || '').trim();
            const formCode = String(this.formInput || '').trim();
            const currentPhone = this.$route.query.phone || '';
            const currentForm = this.$route.query.form_code || '';
            if (trimmedPhone === currentPhone && formCode === currentForm) return;

            const query = { ...this.$route.query, page: 1 };
            if (trimmedPhone) {
                query.phone = trimmedPhone;
            } else {
                delete query.phone;
            }
            if (formCode) {
                query.form_code = formCode;
            } else {
                delete query.form_code;
            }
            this.$router.replace({ query });
        },

        async loadFormOptions() {
            try {
                const codes = await this.$store.dispatch('forms/fetchFormCodes');
                this.formOptions = [
                    { value: '', label: 'Все формы' },
                    ...(codes || []).map(code => ({ value: code, label: code })),
                ];
            } catch (e) {
                // список форм недоступен — остаётся только «Все формы»
            }
        },

        async loadData() {
            this.loading = true;
            try {
                const params = {
                    page: this.activePage,
                    pageSize: this.pageSize,
                };
                if (this.statusFilter !== 'all') {
                    params.status = this.statusFilter;
                }
                if (this.$route.query.form_code) {
                    params.form_code = this.$route.query.form_code;
                }
                if (this.$route.query.phone) {
                    params.phone = this.$route.query.phone;
                }
                const response = await this.$store.dispatch('forms/fetchList', params);
                this.items = response.results || [];
                this.total = response.total || 0;
            } catch (e) {
                this.$root.$emit('showError', 'Ошибка загрузки результатов форм.');
            } finally {
                this.loading = false;
            }
        },

        async setStatus(item, status) {
            this.updatingId = item.id;
            try {
                const updated = await this.$store.dispatch('forms/updateStatus', { id: item.id, status });
                const idx = this.items.findIndex(i => i.id === item.id);
                if (idx !== -1) {
                    // customer_id приходит из JOIN в list, но не из updateStatus — сохраняем его
                    this.$set(this.items, idx, { ...this.items[idx], status: updated.status });
                }
                if (status === 'approved') {
                    this.$root.$emit('showSuccess', 'Одобрено. Пользователь создан/синхронизирован в iiko Cards.');
                }
            } catch (e) {
                this.$root.$emit('showError', 'Ошибка изменения статуса');
            } finally {
                this.updatingId = null;
            }
        },

        // ─── iiko categories modal ──────────────────────────────────────────────

        async openIikoModal(item) {
            this.iikoModalItem = item;
            this.iikoModalVisible = true;
            this.iikoCustomer = null;
            this.iikoCustomerError = null;
            this.iikoSelectedCategoryId = '';

            if (!this.iikoCategories.length) {
                await this.loadIikoCategories();
            }

            // используем уже нормализованный телефон из ответа API
            await this.loadIikoCustomer(item.phone_normalized || item.phone);
        },

        async loadIikoCategories() {
            try {
                const { data } = await this.axios.get('/iiko-card/categories');
                this.iikoCategories = data || [];
            } catch (e) {
                // категории недоступны — не критично
            }
        },

        async loadIikoCustomer(phone) {
            this.iikoCustomerLoading = true;
            this.iikoCustomerError = null;
            try {
                const { data } = await this.axios.get('/iiko-card/customer', { params: { phone } });
                this.iikoCustomer = data;
            } catch (e) {
                this.iikoCustomerError = e?.response?.data?.error || 'Клиент не найден в iiko';
            } finally {
                this.iikoCustomerLoading = false;
            }
        },

        async addIikoCategory() {
            if (!this.iikoSelectedCategoryId || !this.iikoCustomer) return;
            this.iikoActionLoading = true;
            try {
                await this.axios.post(`/iiko-card/customer/${this.iikoCustomer.id}/category/add`, {
                    categoryId: this.iikoSelectedCategoryId,
                });
                await this.loadIikoCustomer(this.iikoModalItem.phone_normalized || this.iikoModalItem.phone);
                this.iikoSelectedCategoryId = '';
            } catch (e) {
                this.$root.$emit('showError', e?.response?.data?.error || 'Ошибка добавления категории');
            } finally {
                this.iikoActionLoading = false;
            }
        },

        async removeIikoCategory(categoryId) {
            if (!this.iikoCustomer) return;
            this.iikoActionLoading = true;
            try {
                await this.axios.post(`/iiko-card/customer/${this.iikoCustomer.id}/category/remove`, {
                    categoryId,
                });
                await this.loadIikoCustomer(this.iikoModalItem.phone_normalized || this.iikoModalItem.phone);
            } catch (e) {
                this.$root.$emit('showError', e?.response?.data?.error || 'Ошибка удаления категории');
            } finally {
                this.iikoActionLoading = false;
            }
        },
        normalizePhone(phone) {
            if (!phone) return null;
            const digits = String(phone).replace(/\D/g, '');
            if (!digits) return null;

            let normalized = digits;
            if (normalized.length === 11 && normalized.startsWith('8')) {
                normalized = `7${normalized.slice(1)}`;
            } else if (normalized.length === 10) {
                normalized = `7${normalized}`;
            } else if (!normalized.startsWith('7')) {
                normalized = `7${normalized}`;
            }

            return `+${normalized}`;
        }
    },
};
</script>

<style scoped>
.table__preloader {
    display: flex;
    width: 100%;
    height: 100px;
    justify-content: center;
    align-items: center;
}

.img-preview {
    position: relative;
    display: inline-block;
}

.img-thumb {
    width: 100px;
    height: 100px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #e4e7ea;
    cursor: zoom-in;
    display: block;
}

.img-full {
    display: none;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 75vw;
    max-height: 75vh;
    object-fit: contain;
    border-radius: 10px;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.55);
    z-index: 9999;
    pointer-events: none;
}

.img-preview:hover .img-full {
    display: block;
}

.pairs-list {
    max-width: 520px;
}

.pair-row {
    margin-bottom: 6px;
    font-size: 13px;
    line-height: 17px;
}

.pair-row:last-child {
    margin-bottom: 0;
}

.gap-1 {
    gap: 4px;
}

.gap-2 {
    gap: 8px;
}

.badge-cat {
    background: #dbeafe;
    color: #1e40af;
    font-size: 13px;
    padding: 4px 8px;
    border-radius: 4px;
    gap: 6px;
}

.btn-remove-cat {
    background: none;
    border: none;
    padding: 0;
    line-height: 1;
    font-size: 16px;
    font-weight: bold;
    color: #93c5fd;
    cursor: pointer;
}

.btn-remove-cat:hover:not(:disabled) {
    color: #ef4444;
}

.btn-remove-cat:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.customer-link {
    font-size: 13px;
    text-decoration: none;
    opacity: 0.7;
}

.customer-link:hover {
    opacity: 1;
}
</style>
